import { reportException } from 'src/utils/errorReporting';

export type SettledQuery<T> =
  | { ok: true; result: T }
  | { ok: false; error: unknown };

const QUERY_LIFECYCLE_ERROR_PREFIX = 'RTK Query';

const getNumericStatus = (value: unknown): number | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.status === 'number') {
    return record.status;
  }

  if (
    record.response &&
    typeof record.response === 'object' &&
    typeof (record.response as { status?: unknown }).status === 'number'
  ) {
    return (record.response as { status: number }).status;
  }

  return undefined;
};

export const getQueryLifecycleHttpStatus = (
  err: unknown,
): number | undefined => {
  const fromRoot = getNumericStatus(err);
  if (fromRoot != null) {
    return fromRoot;
  }

  if (typeof err === 'object' && err !== null && 'error' in err) {
    return getNumericStatus((err as { error: unknown }).error);
  }

  return undefined;
};

export const queryLifecycleErrorMessage = (endpoint: string): string =>
  `${QUERY_LIFECYCLE_ERROR_PREFIX} ${endpoint} failed`;

const reportQueryLifecycleFailure = (
  error: unknown,
  endpoint: string,
): void => {
  const status = getQueryLifecycleHttpStatus(error);
  const tags: Record<string, string> = {
    rtk_query_endpoint: endpoint,
  };
  if (status != null) {
    tags.http_status = String(status);
  }

  reportException(new Error(queryLifecycleErrorMessage(endpoint)), {
    tags,
    extra: { originalError: error },
    fingerprint: ['rtk-query-lifecycle', endpoint],
  });
};

/**
 * Await an RTK Query lifecycle promise without ever rejecting. Failures are
 * reported as a real Error tagged with the endpoint and HTTP status so they
 * group per endpoint instead of collapsing into one stackless rejection.
 */
export const settleQueryFulfilled = async <T>(
  queryFulfilled: Promise<T>,
  endpoint: string,
): Promise<SettledQuery<T>> => {
  try {
    return { ok: true, result: await queryFulfilled };
  } catch (error) {
    try {
      reportQueryLifecycleFailure(error, endpoint);
    } catch {
      // Reporting must not turn a settled failure into a new rejection.
    }
    return { ok: false, error };
  }
};
